import React, { useEffect, useState } from "react";
import { Button, DatePicker, Drawer, Input, InputNumber, Select, Space, Tag, Typography } from "@arco-design/web-react";
import moment from "moment";

const { Text, Title } = Typography;
const { TextArea } = Input;

// Convert a "YYYY-MM-DD" picker value to an RFC3339 timestamp the Go date column
// accepts (a bare date string won't unmarshal into time.Time).
const toISODate = (ds) => (ds ? new Date(ds + "T00:00:00Z").toISOString() : null);

// Presentational card-detail drawer. All persistence is delegated to the parent
// via the on* handlers; this component only renders and collects input.
export default function CardDrawer({ visible, card, stages, members, trackValue, onClose, onMove, onAddNote, onSetOwner, onSetValue }) {
  const [note, setNote] = useState("");

  // Deal-value editor state, re-seeded whenever a different card opens.
  const [amount, setAmount] = useState(undefined);
  const [expectedClose, setExpectedClose] = useState(undefined);
  const [probability, setProbability] = useState(undefined);
  const cardId = card && card.id;
  useEffect(() => {
    setAmount(card && card.amount != null ? card.amount : undefined);
    setExpectedClose(card && card.expected_close_date ? moment(card.expected_close_date).format("YYYY-MM-DD") : undefined);
    setProbability(card && card.probability != null ? card.probability : undefined);
  }, [cardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveValue = () => {
    onSetValue(
      amount == null ? null : amount,
      toISODate(expectedClose),
      probability == null ? null : probability,
    );
  };

  const memberName = (userId) => {
    if (!userId) return null;
    const m = (members || []).find((x) => x.id === userId);
    return m ? (m.name || m.email) : null;
  };

  const submitNote = () => {
    if (!note.trim()) return;
    onAddNote(note.trim());
    setNote("");
  };

  const describeEvent = (e) => {
    if (e.type === "stage_change") {
      const who = memberName(e.user_id) || "Someone";
      const from = e.from_stage_name || "—";
      const to = e.to_stage_name || "—";
      return `${who} moved ${from} → ${to}`;
    }
    if (e.type === "note") {
      const who = memberName(e.user_id) || "Someone";
      return `${who} added a note`;
    }
    return e.type;
  };

  const c = card || {};
  const stageOptions = (stages || []).map((s) => ({
    label: s.is_terminal ? `${s.name} (terminal)` : s.name,
    value: s.id,
  }));
  const memberOptions = (members || []).map((m) => ({ label: m.name || m.email, value: m.id }));

  return (
    <Drawer
      width={440}
      visible={visible}
      onCancel={onClose}
      footer={null}
      title={c.contact_name || c.contact_email || "Card"}
    >
      {card && (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div>
            <Text type="secondary">{c.contact_email}</Text>
            <div style={{ marginTop: 8 }}>
              {c.stage_name && <Tag color="arcoblue">{c.stage_name}</Tag>}
              <Tag color={c.status === "completed" ? "gray" : "green"} style={{ marginLeft: 4 }}>
                {c.status}
              </Tag>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Stage</Text></div>
            <Select
              style={{ width: "100%" }}
              value={c.current_stage_id}
              onChange={(stageId) => stageId !== c.current_stage_id && onMove(stageId)}
              options={stageOptions}
            />
          </div>

          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Owner</Text></div>
            <Select
              allowClear
              placeholder="Unassigned"
              style={{ width: "100%" }}
              value={c.owner_user_id || undefined}
              onChange={(userId) => onSetOwner(userId || null)}
              options={memberOptions}
            />
          </div>

          {trackValue && (
            <div>
              <Title heading={6} style={{ marginBottom: 8 }}>Deal</Title>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <InputNumber
                  style={{ width: "100%" }}
                  prefix="$"
                  min={0}
                  placeholder="Amount"
                  value={amount}
                  onChange={setAmount}
                />
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Expected close date"
                  value={expectedClose}
                  onChange={(ds) => setExpectedClose(ds || undefined)}
                />
                <InputNumber
                  style={{ width: "100%" }}
                  suffix="%"
                  min={0}
                  max={100}
                  placeholder="Win probability (optional — else stage default)"
                  value={probability}
                  onChange={setProbability}
                />
                <div style={{ textAlign: "right" }}>
                  <Button type="primary" size="small" onClick={saveValue}>Save deal</Button>
                </div>
              </Space>
            </div>
          )}

          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Add note</Text></div>
            <TextArea value={note} onChange={setNote} placeholder="What happened?" autoSize={{ minRows: 2 }} />
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <Button type="primary" size="small" disabled={!note.trim()} onClick={submitNote}>Add note</Button>
            </div>
          </div>

          <div>
            <Title heading={6} style={{ marginBottom: 8 }}>Activity</Title>
            {(c.events || []).length === 0 && <Text type="secondary">No activity yet.</Text>}
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {(c.events || []).map((e) => (
                <div key={e.id} style={{ borderLeft: "2px solid var(--color-border-3)", paddingLeft: 10 }}>
                  <div><Text style={{ fontSize: 13 }}>{describeEvent(e)}</Text></div>
                  {e.note && <div style={{ fontSize: 13, color: "var(--color-text-2)", marginTop: 2 }}>“{e.note}”</div>}
                  <div style={{ fontSize: 11, color: "var(--color-text-3)", marginTop: 2 }}>
                    {e.created_date ? moment(e.created_date).format("MMM D, YYYY h:mm A") : ""}
                  </div>
                </div>
              ))}
            </Space>
          </div>
        </Space>
      )}
    </Drawer>
  );
}
